import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock, Building2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHeader from '@/components/common/SectionHeader';
import apiService from '@/services/api';

const ContactPage = () => {
  const [contactInfo, setContactInfo] = useState<any[]>([]);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '', sector: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.id]: e.target.value }));
  };
  const handleSelect = (field: string) => (val: string) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.message) {
      setSubmitError('Please fill in First Name, Email and Message.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await apiService.post('/contact', form);
      if (res.success) {
        setSubmitted(true);
      } else {
        setSubmitError(res.message || 'Failed to send message. Please try again.');
      }
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await apiService.get('/public/contact-info');
        if (response?.data) {
          const data = response.data;
          const formattedInfo = [
            {
              icon: Phone,
              title: 'Phone',
              details: [data.phone || ''],
            },
            {
              icon: Mail,
              title: 'Email',
              details: data.email ? [data.email] : [],
            },
            {
              icon: MapPin,
              title: 'Address',
              details: [
                data.address_line_1 || '',
                `${data.city || ''} ${data.postcode || ''}`.trim(),
              ].filter(Boolean),
            },
            {
              icon: Clock,
              title: 'Hours',
              details: [
                data.business_hours_monday || 'Mon-Fri: 8am - 6pm',
                data.business_hours_saturday || 'Sat: 9am - 1pm',
              ].filter(Boolean),
            },
          ];
          setContactInfo(formattedInfo);
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
        // AUDIT FIX: Show error state instead of hardcoded defaults
        // This ensures admin knows that contact info is not properly configured
        setContactError('Contact information not configured. Please contact support or check back later.');
        setContactInfo(null);
      } finally {
        setContactLoading(false);
      }
    };
    fetchContactInfo();
  }, []);
  return (
    <Layout>
      <Helmet>
        <title>Contact Us | Virewire - We'll Help Get You Started</title>
        <meta name="description" content="Have questions? Our team is here to help. Contact Virewire to learn more about our managed property services platform." />
      </Helmet>

      {/* Hero */}
      <section className="bg-primary text-primary-foreground py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-primary-foreground mb-4">Get In Touch</h1>
            <p className="text-xl text-primary-foreground/80 mb-2">
              Have questions? Our team is ready to help.
            </p>
            <p className="text-lg text-primary-foreground/70">
              Or request a service directly and we'll take it from there.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-card-lg">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-2xl font-semibold text-primary mb-2">Tell Us What You Need</h2>
                  <p className="text-gray-600 mb-6">Whether you have questions or want to request a service, fill out the form below and we'll get back to you within 2 hours.</p>
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <h3 className="text-xl font-semibold text-primary mb-2">Message Sent!</h3>
                      <p className="text-gray-600">Thank you for getting in touch. We'll get back to you within 2 hours.</p>
                    </div>
                  ) : (
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName" className="text-sm font-medium text-primary mb-2 block">First Name *</Label>
                        <Input id="firstName" placeholder="John" value={form.firstName} onChange={handleChange} required />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-sm font-medium text-primary mb-2 block">Last Name *</Label>
                        <Input id="lastName" placeholder="Smith" value={form.lastName} onChange={handleChange} />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-primary mb-2 block">Email Address *</Label>
                        <Input id="email" type="email" placeholder="john@company.com" value={form.email} onChange={handleChange} required />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-sm font-medium text-primary mb-2 block">Phone Number</Label>
                        <Input id="phone" type="tel" placeholder="07700 900000" value={form.phone} onChange={handleChange} />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company" className="text-sm font-medium text-primary mb-2 block">Company Name</Label>
                        <Input id="company" placeholder="ABC Properties Ltd" value={form.company} onChange={handleChange} />
                      </div>
                      <div>
                        <Label htmlFor="sector" className="text-sm font-medium text-primary mb-2 block">Sector</Label>
                        <Select value={form.sector} onValueChange={handleSelect('sector')}>
                          <SelectTrigger><SelectValue placeholder="Select your sector" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="letting">Letting Agent</SelectItem>
                            <SelectItem value="landlord">Landlord</SelectItem>
                            <SelectItem value="property">Property Manager</SelectItem>
                            <SelectItem value="facilities">Facilities Manager</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="education">Education</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-sm font-medium text-primary mb-2 block">Subject</Label>
                      <Select value={form.subject} onValueChange={handleSelect('subject')}>
                        <SelectTrigger><SelectValue placeholder="What can we help with?" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quote">Request a Quote</SelectItem>
                          <SelectItem value="partnership">Partnership Enquiry</SelectItem>
                          <SelectItem value="support">Customer Support</SelectItem>
                          <SelectItem value="general">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm font-medium text-primary mb-2 block">Message *</Label>
                      <Textarea id="message" placeholder="Tell us about your requirements..." rows={6} value={form.message} onChange={handleChange} required />
                    </div>

                    {submitError && <p className="text-sm text-red-600">{submitError}</p>}

                    <Button type="submit" variant="accent" size="lg" className="w-full sm:w-auto" disabled={submitting}>
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                {contactLoading ? (
                  // Loading state
                  [1, 2, 3, 4].map((i) => (
                    <Card key={i} className="shadow-card animate-pulse">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-primary/10 rounded w-20 mb-3"></div>
                            <div className="h-3 bg-primary/10 rounded w-full mb-2"></div>
                            <div className="h-3 bg-primary/10 rounded w-3/4"></div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : contactError ? (
                  <div className="bg-destructive/10 rounded-lg p-4">
                    <p className="text-destructive text-sm font-semibold">{contactError}</p>
                  </div>
                ) : (
                  contactInfo.map((item) => (
                    <Card key={item.title} className="shadow-card">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                            <item.icon className="h-5 w-5 text-accent" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-primary mb-1">{item.title}</h4>
                          {item.details.map((detail, i) => (
                            <p key={i} className="text-sm text-muted-foreground">{detail}</p>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  ))
                )}

                <Card className="shadow-card bg-primary text-primary-foreground">
                  <CardContent className="p-5">
                    <Building2 className="h-8 w-8 mb-3 text-accent" />
                    <h4 className="font-semibold mb-2">For Commercial Clients</h4>
                    <p className="text-sm text-primary-foreground/80 mb-4">
                      Looking for volume discounts or a dedicated account manager? 
                      We offer tailored solutions for larger portfolios.
                    </p>
                    <Button variant="heroOutline" size="sm" className="w-full">
                      Request Partnership Info
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="section-padding bg-muted">
        <div className="container-custom text-center">
          <SectionHeader 
            title="Have Questions?"
            subtitle="Check our frequently asked questions or reach out to our team for immediate assistance."
          />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="accent" size="lg" asChild>
              <a href="tel:08001234567">Call Us Now</a>
            </Button>
            <Button variant="outline" size="lg">
              View FAQs
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
